import {Button, Cell, Headline, Radio, Section, Switch} from "@telegram-apps/telegram-ui";
import {useAuthProfile} from "@/providers/AuthProfileProvider.tsx";
import {useTranslation} from "react-i18next";
import React, {useState} from "react";
import {updateUserSettings} from "@/api/apiMethods.ts";
import {useNavigation} from "@/providers/NavigationProvider.tsx";
import {TbCheck, TbTrash} from "react-icons/tb";
import {
  SectionHeader
} from "@telegram-apps/telegram-ui/dist/components/Blocks/Section/components/SectionHeader/SectionHeader";
import {ModalDatePicker} from "@/components/ModalDatePicker/ModalDatePicker.tsx";
import {haptic} from "@/helpers/haptic.ts";

export const UserSettingsPage = () => {
  const {authProfile, setAuthProfile} = useAuthProfile();
  const {t} = useTranslation();
  const {showSnackbar, navigateToRoute} = useNavigation();
  const [showBookings, setShowBookings] = useState<"none" | "anonymous" | "public">(authProfile?.user.showBookings || 'anonymous');
  const [showTgUsername, setShowTgUsername] = useState<boolean>(authProfile?.user.showTgUsername ?? Boolean(authProfile?.user.tgUsername));
  const [showGiftedWishes, setShowGiftedWishes] = useState<boolean>(authProfile?.user.showGiftedWishes ?? true);
  const [birthday, setBirthday] = useState<Date | null>(authProfile?.user.birthday || null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  if (!authProfile) {
    throw new Error("UserSettingsPage must be inside AuthProfileProvider");
  }

  const onShowBookingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    haptic.selection();
    // @ts-expect-error it's ok
    setShowBookings(event.target.value);
  };

  const onShowTgUsernameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    haptic.selection();
    setShowTgUsername(event.target.checked);
  };

  const onShowGiftedWishesChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    haptic.selection();
    setShowGiftedWishes(event.target.checked);
  };

  const saveSettings = async () => {
    setIsSaving(true);
    try {
      const newSettings = {
        language: authProfile.user.language,
        showBookings: showBookings,
        showTgUsername: authProfile.user.tgUsername ? showTgUsername : false,
        showGiftedWishes: showGiftedWishes,
        birthday: birthday,
        countryCode: authProfile.user.countryCode,
        city: authProfile.user.city,
      };
      
      const updatedSettings = await updateUserSettings(newSettings);
      const newUser = {...authProfile.user, ...updatedSettings};
      setAuthProfile({...authProfile, user: newUser});
      haptic.notification('success');
      showSnackbar("✅", "system.settingsSaved");
      navigateToRoute("/main");
    } catch (e) {
      haptic.notification('error');
      showSnackbar("❌", "system.unknowError");
    }
    setIsSaving(false);
  };

  const onBirthdaySaved = async (date: Date) => {
    setBirthday(date);
    setIsDatePickerOpen(false);
  }

  const openDatePicker = () => {
    haptic.impact('light');
    setIsDatePickerOpen(true);
  }

  const onBirthdayCancel = () => {
    setIsDatePickerOpen(false);
  }

  const onBirthdayDelete = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    haptic.impact('light');
    setBirthday(null);
  }

  const formatBirthdayForCompare = (date: Date | null | undefined) => date?.toDateString() ?? null;
  const hasTelegramUsername = Boolean(authProfile.user.tgUsername);
  const hasChanges = showBookings !== authProfile.user.showBookings
    || formatBirthdayForCompare(birthday) !== formatBirthdayForCompare(authProfile.user.birthday)
    || (hasTelegramUsername && showTgUsername !== (authProfile.user.showTgUsername ?? true))
    || showGiftedWishes !== (authProfile.user.showGiftedWishes ?? true);

  return (
    <div className={"p-3 pb-10"}>
      <Headline className={"pl-2 text-center mb-3"} weight={"2"}>{t('profile.settings')}</Headline>

      <Section header={<SectionHeader>{t('settings.profileData')}</SectionHeader>}>
        <Cell subhead={t('profile.birthday')}
              onClick={openDatePicker}
              after={<div className={"flex gap-2"}>
                <Button mode={"plain"}>{t('action.edit')}</Button>
                {birthday && <Button mode={"plain"}
                                     before={<TbTrash size={18}/>}
                                     onClick={onBirthdayDelete}>
                  {t('action.clear')}
                </Button>}
              </div>}>
          {birthday?.toLocaleDateString(authProfile.user.language, {year: "numeric", month: "long", day: "numeric"}) ?? " — "}
        </Cell>
        <Cell Component="label"
              multiline={true}
              description={hasTelegramUsername
                ? t('settings.tgUsernameVisibility.description')
                : t('settings.tgUsernameVisibility.disabledDescription')}
              after={<Switch checked={hasTelegramUsername && showTgUsername}
                             disabled={!hasTelegramUsername}
                             onChange={onShowTgUsernameChange}
              />}>
          {t('settings.tgUsernameVisibility.value')}
        </Cell>
        <Cell Component="label"
              multiline={true}
              description={t('settings.giftedWishesVisibility.description')}
              after={<Switch checked={showGiftedWishes}
                             onChange={onShowGiftedWishesChange}
              />}>
          {t('settings.giftedWishesVisibility.value')}
        </Cell>
      </Section>
      <Section header={<SectionHeader>{t('settings.bookedWishes')}</SectionHeader>}>
        <div className={"mb-3"}>
          <Cell Component="label"
                multiline={true}
                description={t('settings.hideBookings.description')}
                before={<Radio name="showBookings"
                               value="none"
                               checked={showBookings === "none"}
                               onChange={onShowBookingsChange}
                />}>
            {t('settings.hideBookings.value')}
          </Cell>
          <Cell Component="label"
                multiline={true}
                description={t('settings.anonymousBookings.description')}
                before={<Radio name="showBookings"
                             value="anonymous"
                             checked={showBookings === "anonymous"}
                             onChange={onShowBookingsChange}
                />}>
            {t('settings.anonymousBookings.value')}
          </Cell>
          <Cell Component="label"
                multiline={true}
                description={t('settings.publicBookings.description')}
                before={<Radio name="showBookings"
                             value="public"
                             checked={showBookings === "public"}
                             onChange={onShowBookingsChange}
                />}>
            {t('settings.publicBookings.value')}
          </Cell>
        </div>
      </Section>

      <Button stretched={true}
              before={<TbCheck size={20}/>}
              loading={isSaving}
              disabled={isSaving || !hasChanges}
              onClick={saveSettings}
              mode={"filled"}>
        {t('action.save')}
      </Button>

      <ModalDatePicker
        opened={isDatePickerOpen}
        initialDate={birthday}
        onApply={onBirthdaySaved}
        onCancel={onBirthdayCancel}/>
    </div>
  );
};
